import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { Loading } from '@components/ui/loading/loading';
import { HostConfigurationOverlayService } from '@6grain/masofa-monolith-api-client/common/api/hostConfigurationOverlay.service';
import { HostConfigurationOverlayListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/hostConfigurationOverlayListItemResponse';
import { SaveHostConfigurationOverlayRequest } from '@6grain/masofa-monolith-api-client/common/model/saveHostConfigurationOverlayRequest';
import { ConfigJsonFieldsComponent } from './config-json-fields';
import { ConfigField, ConfigListChange, appendArrayItem, applyFieldValue, fieldsFromJson, removeArrayItem } from './config-json-form';

@Component({
  selector: 'app-orchestration-command-config-detail',
  standalone: true,
  imports: [
    TranslateModule,
    PrimaryButton,
    SecondaryButton,
    Loading,
    ConfigJsonFieldsComponent,
  ],
  templateUrl: './orchestration-command-config-detail.html',
  styleUrl: './orchestration-command-config-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationCommandConfigDetailComponent {
  private readonly api = inject(HostConfigurationOverlayService);
  private readonly destroyRef = inject(DestroyRef);

  readonly sectionName = input.required<string>();
  readonly saved = output<void>();

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly section = signal<HostConfigurationOverlayListItemResponse | null>(null);
  readonly draft = signal<unknown>({});
  readonly fields = signal<ConfigField[]>([]);

  readonly heading = computed(() => this.section()?.displayName || this.section()?.sectionName || '');
  readonly subtitle = computed(() => {
    const item = this.section();
    if (!item?.displayName) {
      return '';
    }
    return item.sectionName ?? '';
  });
  readonly canRevert = computed(() => this.section()?.source === 'Overlay');
  readonly description = computed(() => this.section()?.description ?? '');
  readonly restartRequired = computed(() => this.section()?.restartRequired === true);

  constructor() {
    effect(() => {
      const sectionName = this.sectionName();
      this.load(sectionName);
    });
  }

  private load(sectionName: string): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.api
      .orchestrationCommandConfigurationGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => {
          const match = (items ?? []).find(item => item.sectionName === sectionName) ?? null;
          this.section.set(match);
          if (!match) {
            this.errorMessage.set('Failed to load command configuration');
          } else {
            this.bindDraft(match.effectiveJson);
          }
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('Failed to load command configuration');
          this.loading.set(false);
        },
      });
  }

  onField(field: ConfigField): void {
    const next = applyFieldValue(this.draft(), field.path, field.value, field.kind);
    this.draft.set(next);
    this.fields.set(fieldsFromJson(next));
  }

  onList(change: ConfigListChange): void {
    const next = change.op === 'add'
      ? appendArrayItem(this.draft(), change.path)
      : removeArrayItem(this.draft(), change.path);
    this.draft.set(next);
    this.fields.set(fieldsFromJson(next));
  }

  save(): void {
    const sectionName = this.section()?.sectionName;
    if (!sectionName) {
      return;
    }
    this.saving.set(true);
    this.errorMessage.set('');
    const body: SaveHostConfigurationOverlayRequest = {
      json: this.draft() as SaveHostConfigurationOverlayRequest['json'],
    };
    this.api.orchestrationCommandConfigurationSectionNamePut(sectionName, body).subscribe({
      next: item => {
        this.section.set(item);
        this.bindDraft(item.effectiveJson);
        this.saving.set(false);
        this.saved.emit();
      },
      error: () => {
        this.errorMessage.set('Failed to save command configuration');
        this.saving.set(false);
      },
    });
  }

  revertToHost(): void {
    const sectionName = this.section()?.sectionName;
    if (!sectionName || !this.canRevert()) {
      return;
    }
    this.saving.set(true);
    this.errorMessage.set('');
    this.api.orchestrationCommandConfigurationSectionNameDelete(sectionName).subscribe({
      next: item => {
        this.section.set(item);
        this.bindDraft(item.effectiveJson);
        this.saving.set(false);
        this.saved.emit();
      },
      error: () => {
        this.errorMessage.set('Failed to revert command configuration');
        this.saving.set(false);
      },
    });
  }

  private bindDraft(effectiveJson: string | null | undefined): void {
    let parsed: unknown = {};
    try {
      parsed = JSON.parse(effectiveJson || '{}');
    } catch {
      this.errorMessage.set('JSON is invalid');
      parsed = {};
    }
    this.draft.set(parsed);
    this.fields.set(fieldsFromJson(parsed));
  }
}
